import type { Message } from '../../i18n/types';

/** The project's coverage card: do the test cases' autotests actually run. */
export const coverageEn = {
  'coverage.title': 'Autotest coverage',
  'coverage.description':
    'A case counts as covered when its autotest passed in the last {days} days. Red, skipped and not run are not coverage.',
  'coverage.days': '{days} days',
  'coverage.headline': '{working} of {cases} cases checked by autotests',
  'coverage.linkedNotWorking': {
    one: '{count} more has an autotest that does not check it',
    other: '{count} more have an autotest that does not check them',
  },
  'coverage.withoutAutotest': {
    one: '{count} without an autotest',
    other: '{count} without an autotest',
  },
  'coverage.status.working': 'Works',
  'coverage.status.partial': 'Partly',
  'coverage.status.gone': 'Link broken',
  'coverage.status.red': 'Red',
  'coverage.status.skipped': 'Skipped',
  'coverage.status.noData': 'Not run',
  'coverage.status.none': 'No autotest',
  'coverage.status.working.hint': 'every linked autotest passed in its latest run',
  'coverage.status.partial.hint':
    'the case is linked to a whole class whose tests partly failed; link the test itself to know',
  'coverage.status.gone.hint': 'a link names a class or test the code no longer has',
  'coverage.status.red.hint': 'a linked autotest failed or broke',
  'coverage.status.skipped.hint': 'a linked autotest only skipped: no account, a guard',
  'coverage.status.noData.hint': 'a linked autotest did not run on master in the window',
  'coverage.status.none.hint': 'no autotest is linked to the case',
  'coverage.byPriority': 'By priority',
  'coverage.byFolder': 'By folder',
  'coverage.col.group': 'Group',
  'coverage.col.cases': 'Cases',
  'coverage.col.linked': 'With autotest',
  'coverage.attention': 'Cases to look at',
  'coverage.attentionEmpty': 'Nothing here — every case of this kind is fine.',
  'coverage.noLinks': 'No case is linked to an autotest yet',
  'coverage.noLinksHint':
    'Write @TmsLink("KEY") in the autotest or link it on the case page. Repositories are rescanned every night.',
  'coverage.notCollected':
    'TMS has not stored any results of the linked projects yet: it reads finished pipelines every 10 minutes.',
  'coverage.collectedAt': 'results stored {when}',
  'coverage.defect': 'known defect {id}',
  'coverage.knownCause': 'known cause: {id}',
  'coverage.noFolder': 'Without a folder',
  'coverage.passedOf': '{passed} of {total} passed',
  'coverage.goneClass': 'Class {name} is not in the code any more — fix @TmsLink or the link',
  'coverage.goneTest': 'Test {name} is not in the code any more — fix @TmsLink or the link',
  'coverage.refresh': 'Refresh now',
  'coverage.refreshed': {
    one: 'Stored {count} new run',
    other: 'Stored {count} new runs',
  },
  'coverage.refreshedNone': 'No new finished runs',
} satisfies Record<string, Message>;

export const coverageRu: Record<keyof typeof coverageEn, Message> = {
  'coverage.title': 'Покрытие автотестами',
  'coverage.description':
    'Кейс покрыт, если его автотест прошёл за последние {days} дн. Красный, пропущенный и не запущенный — не покрытие.',
  'coverage.days': '{days} дн.',
  'coverage.headline': '{working} из {cases} кейсов проверяются автотестами',
  'coverage.linkedNotWorking': {
    one: 'ещё у {count} автотест есть, но кейс не проверяет',
    few: 'ещё у {count} автотест есть, но кейс не проверяет',
    many: 'ещё у {count} автотест есть, но кейс не проверяет',
    other: 'ещё у {count} автотест есть, но кейс не проверяет',
  },
  'coverage.withoutAutotest': {
    one: '{count} без автотеста',
    few: '{count} без автотеста',
    many: '{count} без автотеста',
    other: '{count} без автотеста',
  },
  'coverage.status.working': 'Работает',
  'coverage.status.partial': 'Частично',
  'coverage.status.gone': 'Связь битая',
  'coverage.status.red': 'Красный',
  'coverage.status.skipped': 'Пропущен',
  'coverage.status.noData': 'Не запускался',
  'coverage.status.none': 'Без автотеста',
  'coverage.status.working.hint': 'все связанные автотесты прошли в последнем прогоне',
  'coverage.status.partial.hint':
    'кейс связан с целым классом, и часть его тестов упала; чтобы знать точно, свяжите кейс с самим тестом',
  'coverage.status.gone.hint': 'связь указывает на класс или тест, которого больше нет в коде',
  'coverage.status.red.hint': 'связанный автотест упал',
  'coverage.status.skipped.hint':
    'связанный автотест только пропускается: нет аккаунта, предохранитель',
  'coverage.status.noData.hint': 'связанный автотест не запускался на master за этот срок',
  'coverage.status.none.hint': 'к кейсу не привязан ни один автотест',
  'coverage.byPriority': 'По приоритету',
  'coverage.byFolder': 'По папкам',
  'coverage.col.group': 'Группа',
  'coverage.col.cases': 'Кейсов',
  'coverage.col.linked': 'С автотестом',
  'coverage.attention': 'Кейсы, на которые стоит посмотреть',
  'coverage.attentionEmpty': 'Здесь пусто — с такими кейсами всё в порядке.',
  'coverage.noLinks': 'Ни один кейс ещё не связан с автотестом',
  'coverage.noLinksHint':
    'Напишите @TmsLink("КЛЮЧ") в автотесте или привяжите его на странице кейса. Репозитории пересканируются каждую ночь.',
  'coverage.notCollected':
    'TMS ещё не сохранил результатов связанных проектов: завершённые пайплайны читаются раз в 10 минут.',
  'coverage.collectedAt': 'результаты сохранены {when}',
  'coverage.defect': 'известный дефект {id}',
  'coverage.knownCause': 'известная причина: {id}',
  'coverage.noFolder': 'Без папки',
  'coverage.passedOf': 'прошло {passed} из {total}',
  'coverage.goneClass': 'Класса {name} больше нет в коде — поправьте @TmsLink или связь',
  'coverage.goneTest': 'Теста {name} больше нет в коде — поправьте @TmsLink или связь',
  'coverage.refresh': 'Обновить сейчас',
  'coverage.refreshed': {
    one: 'Сохранён {count} новый прогон',
    few: 'Сохранено {count} новых прогона',
    many: 'Сохранено {count} новых прогонов',
    other: 'Сохранено {count} новых прогона',
  },
  'coverage.refreshedNone': 'Новых завершённых прогонов нет',
};
